import api from './api.service';
import type { RejectionPayload } from './rejection.service';

export type CreationMode = 'Direct' | 'AgainstSalesOrder';

export interface PurchaseRequisitionLine {
  id?: string;
  line_number: number;
  /** Inventory item id from Item Master (enriched on getById) */
  item_id?: string | null;
  item_code?: string;
  item_description: string;
  item_category?: string;
  line_kind?: 'catalog' | 'description_only' | null;
  requested_quantity: number;
  ordered_quantity?: number;
  uom?: string;
  length?: string | null;
  /** Measurement snapshot: pieces × dimensions (mm) → requested_quantity in UOM */
  measurement_type?: string | null;
  piece_quantity?: number | string | null;
  length_mm?: number | string | null;
  width_mm?: number | string | null;
  height_mm?: number | string | null;
  item_measurement_type?: string | null;
  default_width_mm?: number | string | null;
  default_height_mm?: number | string | null;
  /** Engineering theoretical weight (kg), server-computed. Information only. */
  theoretical_unit_weight?: number | string | null;
  theoretical_weight?: number | string | null;
  theoretical_weight_uom?: string | null;
  engineering_snapshot?: string | null;
  estimated_unit_price?: number;
  estimated_total?: number;
  required_date?: string;
  project_id?: string;
  sales_order_id?: string;
  sales_order_line_id?: string;
  specifications?: string;
  notes?: string;
  /** Joined from Item Master on getById */
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  item_name?: string | null;
  item_type?: string | null;
  base_uom?: string | null;
  purchase_uom?: string | null;
  sales_uom?: string | null;
  is_critical?: boolean | null;
  project?: { id: string; project_name: string };
  salesOrder?: { sales_order_number: string };
  salesOrderItem?: { line_number: number };
}

export interface PurchaseRequisition {
  id: string;
  company_id: string;
  pr_number: string;
  creation_mode: CreationMode;
  pr_date: string;
  required_date: string;
  priority: string;
  requisition_type: string;
  requested_by: string;
  department?: string;
  department_id?: string | null;
  cost_center?: string;
  purpose?: string;
  justification?: string;
  notes?: string;
  currency: string;
  pr_status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  approved_by?: string;
  approved_date?: string;
  rejection_reason?: string;
  estimated_total: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  items?: PurchaseRequisitionLine[];
  requestedByUser?: { id: string; email?: string; full_name?: string };
  project?: { id: string; project_name: string };
}

export interface EligibleSOLine {
  sales_order_id: string;
  sales_order_number: string;
  sales_order_line_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  ordered_quantity: number;
  requested_quantity: number;
  requestable_quantity: number;
  uom: string | null;
  project_id: string | null;
  project_name?: string | null;
  unit_price?: number;
  required_date?: string | null;
  specifications?: string | null;
}

export interface CreatePurchaseRequisitionPayload {
  creation_mode: CreationMode;
  pr_date: string;
  required_date: string;
  priority?: string;
  requisition_type?: string;
  requested_by: string;
  department?: string;
  department_id?: string;
  cost_center?: string;
  purpose?: string;
  justification?: string;
  notes?: string;
  currency?: string;
  lines_direct?: {
    id?: string;
    line_number: number;
    item_code?: string;
    item_description: string;
    item_category?: string;
    line_kind?: 'catalog' | 'description_only';
    requested_quantity: number;
    uom?: string;
    length?: string | null;
    piece_quantity?: number | null;
    length_mm?: number | null;
    width_mm?: number | null;
    height_mm?: number | null;
    estimated_unit_price?: number;
    required_date?: string;
    project_id: string;
    wbs_node_id?: string | null;
    cbs_code_id?: string | null;
    cost_category?: string | null;
    specifications?: string | null;
    notes?: string;
    /** Calculator inputs (dimensions / authorised overrides). The server computes the weight. */
    engineering_input?: import('./engineeringMaterial.service').EngineeringLineInput;
  }[];
  lines_from_so?: {
    id?: string;
    sales_order_id: string;
    sales_order_line_id: string;
    requested_quantity: number;
    project_id?: string;
  }[];
}

export interface PurchaseRequisitionFilters {
  pr_number?: string;
  from_date?: string;
  to_date?: string;
  status?: string;
  project_id?: string;
  requested_by?: string;
  creation_mode?: string;
  sales_order_id?: string;
  /** Free-text across header + line details (item, project, notes, etc.) */
  search?: string;
  item_code?: string;
  item_description?: string;
  priority?: string;
  sales_order_number?: string;
}

function unwrap<T>(response: any): T {
  const data = response?.data ?? response;
  return data?.data ?? data;
}

class PurchaseRequisitionService {
  async list(filters?: PurchaseRequisitionFilters): Promise<PurchaseRequisition[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.append(k, String(v));
      });
    }
    const response = await api.get(`/procurement/purchase-requisitions?${params.toString()}`);
    const data = unwrap<PurchaseRequisition[]>(response);
    return Array.isArray(data) ? data : [];
  }

  async getById(id: string): Promise<PurchaseRequisition> {
    const response = await api.get(`/procurement/purchase-requisitions/${id}`);
    return unwrap<PurchaseRequisition>(response);
  }

  async getEligibleSoLines(filters?: {
    customer_id?: string;
    from_date?: string;
    to_date?: string;
    order_status?: string;
    sales_order_ids?: string[];
  }): Promise<EligibleSOLine[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v == null || v === '') return;
        if (k === 'sales_order_ids' && Array.isArray(v)) {
          v.forEach((id) => params.append('sales_order_ids', id));
        } else {
          params.append(k, String(v));
        }
      });
    }
    const response = await api.get(`/procurement/purchase-requisitions/eligible-so-lines?${params.toString()}`);
    const data = unwrap<EligibleSOLine[]>(response);
    return Array.isArray(data) ? data : [];
  }

  async getNonstockDefaultItem(): Promise<{ item_code: string; note?: string }> {
    const response = await api.get('/procurement/purchase-requisitions/nonstock-default-item');
    return unwrap<{ item_code: string; note?: string }>(response);
  }

  async create(payload: CreatePurchaseRequisitionPayload): Promise<PurchaseRequisition> {
    const response = await api.post('/procurement/purchase-requisitions', payload);
    return unwrap<PurchaseRequisition>(response);
  }

  async update(id: string, payload: Partial<CreatePurchaseRequisitionPayload>): Promise<PurchaseRequisition> {
    const response = await api.put(`/procurement/purchase-requisitions/${id}`, payload);
    return unwrap<PurchaseRequisition>(response);
  }

  async submit(id: string): Promise<PurchaseRequisition> {
    const response = await api.post(`/procurement/purchase-requisitions/${id}/submit`);
    return unwrap<PurchaseRequisition>(response);
  }

  async approve(id: string, remarks?: string): Promise<PurchaseRequisition> {
    const response = await api.post(`/procurement/purchase-requisitions/${id}/approve`, {
      remarks: remarks ?? undefined,
    });
    return unwrap<PurchaseRequisition>(response);
  }

  async reject(id: string, payload: RejectionPayload): Promise<PurchaseRequisition> {
    const response = await api.post(`/procurement/purchase-requisitions/${id}/reject`, payload);
    return unwrap<PurchaseRequisition>(response);
  }

  async revokeApproval(id: string, reason: string): Promise<PurchaseRequisition> {
    const response = await api.post(`/procurement/purchase-requisitions/${id}/revoke-approval`, { reason });
    return unwrap<PurchaseRequisition>(response);
  }

  async cancel(id: string): Promise<PurchaseRequisition> {
    const response = await api.post(`/procurement/purchase-requisitions/${id}/cancel`);
    return unwrap<PurchaseRequisition>(response);
  }
}

export const purchaseRequisitionService = new PurchaseRequisitionService();
export default purchaseRequisitionService;
